/**
 * resolveLook — flatten `theme_config.look` (preset_id + optional overrides)
 * into a fully concrete ResolvedLook bundle.
 *
 * Defensive: if `theme_config.look` is missing, malformed, or names an
 * unknown preset_id, the resolver falls back to a default preset (or to
 * `cozy_boutique` if no fallback is supplied). Unknown override values
 * for a knob are dropped (preset default wins for that knob).
 *
 * Used by:
 *   - `PublicLayout.tsx` to derive Header/Footer/Hero variants + CSS vars
 *   - `ThemeCustomizer.tsx` to populate the Look picker + Advanced UI
 *   - `db-setup.ts` (wizard) to seed `site_settings.theme_config.look`
 */

import { LOOKS, DEFAULT_LOOK_FOR_SITE_TYPE, ALL_PRESET_IDS } from './presets';
import type {
  ButtonShape,
  CardStyle,
  Density,
  FontPairId,
  FooterVariant,
  HeaderVariant,
  HeroVariant,
  LookConfig,
  LookOverrides,
  PaletteFamilyId,
  PresetId,
  RadiusScale,
  ResolvedLook,
} from './types';

const HEADER_VARIANTS: readonly HeaderVariant[] = ['classic', 'centered_logo', 'transparent_overlay', 'mega_menu', 'minimal'];
const HERO_VARIANTS: readonly HeroVariant[] = ['standard', 'split', 'fullscreen_media', 'centered_card', 'search_forward', 'none'];
const FOOTER_VARIANTS: readonly FooterVariant[] = ['multi_column', 'compact', 'big_cta', 'minimal'];
const BUTTON_SHAPES: readonly ButtonShape[] = ['rounded', 'pill', 'square', 'cut_corner'];
const CARD_STYLES: readonly CardStyle[] = ['elevated', 'bordered', 'flat', 'glass'];
const RADIUS_SCALES: readonly RadiusScale[] = ['sharp', 'soft', 'round', 'pill'];
const DENSITIES: readonly Density[] = ['compact', 'comfortable', 'spacious'];
const FONT_PAIR_IDS: readonly FontPairId[] = ['classic_serif', 'editorial', 'modern_sans', 'cozy_warm', 'bold_grotesk', 'museum', 'playful', 'tech'];
const PALETTE_FAMILY_IDS: readonly PaletteFamilyId[] = ['warm_earth', 'cool_slate', 'jewel', 'monochrome', 'pastel', 'vibrant', 'editorial_ivory', 'gradient_tech'];

function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function isPresetId(value: unknown): value is PresetId {
  return typeof value === 'string' && (ALL_PRESET_IDS as readonly string[]).includes(value);
}

/**
 * Resolve a Look from arbitrary JSON. Bad input → default preset, no throw.
 *
 * @param look       Raw `theme_config.look` value (may be undefined / malformed).
 * @param siteType   Optional siteType hint — when no preset is set, the
 *                   default for this siteType is used (falls back to
 *                   `cozy_boutique` if siteType is unknown).
 */
export function resolveLook(look: unknown, siteType?: string): ResolvedLook {
  const fallbackPresetId: PresetId =
    (siteType && DEFAULT_LOOK_FOR_SITE_TYPE[siteType]) || 'cozy_boutique';

  const config = (look && typeof look === 'object' ? (look as Partial<LookConfig>) : {}) as Partial<LookConfig>;
  const presetId: PresetId = isPresetId(config.preset_id) ? config.preset_id : fallbackPresetId;
  const preset = LOOKS[presetId];
  const overrides: LookOverrides =
    config.overrides && typeof config.overrides === 'object' ? config.overrides : {};

  // `search_forward` is a FUNCTIONAL hero — a photo with a dates/guests
  // availability search bar front and centre — not an aesthetic. It only makes
  // sense where there are properties to search. The `hospitality_warm` preset
  // carries it, and the wizard's look-picker rightly chooses that preset for
  // anything hospitality-flavoured, so a SaaS product *for* hosts (innscreen,
  // 2026-09-09) launched with a vacation-rental booking widget on its homepage.
  // Keep the Look; swap just the hero for the nearest photo-led variant. Only
  // when the caller says what the site is: an unknown siteType is left alone.
  let heroVariant = pick(overrides.hero_variant, HERO_VARIANTS, preset.hero_variant);
  if (heroVariant === 'search_forward' && siteType !== undefined && siteType !== 'lodging') {
    heroVariant = 'fullscreen_media';
  }

  return {
    preset_id: presetId,
    header_variant: pick(overrides.header_variant, HEADER_VARIANTS, preset.header_variant),
    hero_variant: heroVariant,
    footer_variant: pick(overrides.footer_variant, FOOTER_VARIANTS, preset.footer_variant),
    button_shape: pick(overrides.button_shape, BUTTON_SHAPES, preset.button_shape),
    card_style: pick(overrides.card_style, CARD_STYLES, preset.card_style),
    radius_scale: pick(overrides.radius_scale, RADIUS_SCALES, preset.radius_scale),
    density: pick(overrides.density, DENSITIES, preset.density),
    font_pair_id: pick(overrides.font_pair_id, FONT_PAIR_IDS, preset.font_pair_id),
    palette_family_id: pick(overrides.palette_family_id, PALETTE_FAMILY_IDS, preset.palette_family_id),
  };
}
